import Vue from 'vue';
import { make } from 'vuex-pathify';
import i18n from '@/plugins/i18n';

import api from '@/services/api';
import {
  arrayToObject,
  objectToArray,
  saveFileFromBackend,
  findLayerByLayerId,
  getFlatGroupsLayers,
  findLayerIndexByLayerId,
  groupByProperty,
  mapProject,
} from '@/assets/js/utility';
import { defaultSRID } from '@/assets/js/variables';

const state = {
  metadata: {},
  additionalMetadata: {},
  project: undefined,
  unchangedProject: undefined,
  projects: {},
  projectsPreviewsToUpdate: [],
  projectId: undefined,
  currentLayer: undefined,
  currentLayerSelectedObjects: {},
  currentLayerSelectedObjectsIds: [],
  currentLayerMetadata: undefined,
  dataSourcesForms: {},
  featuresLayers: {},
  dataSourcesFeatures: {},
  layersFeatures: {},
  layersFilters: {},
  layers: {},
  databoxServiceLayers: [],
  databoxMvtLayers: [],
  layersCount: {},
  basemapLayers: {},
  dataTypes: [],
  layersSchemaByScope: {},
  expressions: [],
  icons: [],
  templateIcons: [],
  currentLayerSchema: [],
  defaultValuesPolicies: undefined,
  measurementUnits: {},
  relationValuesMapping: {},
  easements: {},
  folders: {},
  folderDocuments: {},
  cacheLayersMetadata: {},
  additionalAttributesValues: undefined,
  temporaryLayers: [],
};

const mutations = {
  ...make.mutations(state),
  SET_PROJECT_ID(state, value) {
    const currentUserId = this.state.users.currentUser.id;
    const stringifiedUsersProjects = JSON.stringify({
      ...JSON.parse(localStorage.getItem('usersProjects')),
      [currentUserId]: value,
    });
    localStorage.setItem('usersProjects', stringifiedUsersProjects);
    state.projectId = value;
  },
  SET_LAYERS: (state, value) => {
    state.layers = { ...state.layers, ...value };
  },
  SET_LAYER(state, { layer_id: id, body, scope } = {}) {
    // eslint-disable-next-line
    const { permissions, ...restBody } = body;
    Vue.set(state.layers, id, { ...state.layers[id], ...restBody });

    if (scope && scope !== 'module' && state.layersSchemaByScope[scope]) {
      const mapLayer = layer => {
        return layer.id === id ? { ...layer, ...(restBody['name'] ? { name: restBody['name'] } : {}) } : layer;
      };
      const newScopeState = state.layersSchemaByScope[scope].map(element => {
        if (element.layers) {
          return { ...element, layers: element.layers.map(mapLayer) };
        } else {
          return mapLayer(element);
        }
      });
      Vue.set(state.layersSchemaByScope, scope, newScopeState);
    }
  },
  ADD_PROJECT_GROUP: (state, data) => {
    const { value, id } = data;
    const group = { name: value, type: 'group', layers: [], id };
    Vue.set(state.project, 'layers', [...state.project.layers, ...[group]]);
  },
  CHANGE_GROUP_NAME: (state, data) => {
    const { index, name } = data;
    Vue.set(state.project.layers[index], 'name', name);
  },
  ADD_PROJECT_LAYERS: (state, value) => {
    Vue.set(state.project, 'layers', [...state.project.layers, ...value]);
  },
  ADD_DATA_SOURCE_FEATURES: (state, value) => {
    const { dataSource, features } = value;
    Vue.set(state.dataSourcesFeatures, dataSource, features);
  },
  SET_LAYERS_FILTERS: (state, value) => {
    const { layerId, ...rest } = value;
    Vue.set(state.layersFilters, layerId, { ...rest });
  },
  DELETE_LAYERS_FILTERS: (state, value) => {
    Vue.delete(state.layersFilters, value);
  },
  ADD_DATA_SOURCE_FORM: (state, value) => {
    const { dataSource, form } = value;
    Vue.set(state.dataSourcesForms, dataSource, form);
  },
  ADD_METADATA: (state, value) => {
    const { dataSource, metadata } = value;
    Vue.set(state.metadata, dataSource, { ...state.metadata[dataSource], ...metadata });
  },
  DELETE_DATA_SOURCE: (state, value) => {
    Vue.delete(state.metadata, value);
  },
  EDIT_DATA_SOURCE_METADATA: (state, { name, body: { desc_attribute_name, desc_attributes, ...restBody } }) => {
    Vue.set(state.metadata, name, { ...state.metadata[name], ...restBody });
    if (desc_attribute_name)
      Vue.set(state.metadata[name]['attributes_schema'], 'desc_attribute_name', desc_attribute_name);
    if (desc_attributes) Vue.set(state.metadata[name]['attributes_schema'], 'desc_attributes', desc_attributes);
  },
  ADD_LAYER_VISIBILITY: (state, value) => {
    const { dataSource, visible } = value;
    Vue.set(state.layersVisibility, dataSource, visible);
  },
  SET_LAYER_VISIBILITY: (state, value) => {
    const { layerId, visible } = value;
    const layer = findLayerByLayerId(state.project.layers, layerId);
    Vue.set(layer, 'visible', visible);
  },
  SET_ALL_LAYERS_LABELS_VISIBILITY: (state, visible) => {
    const layers = getFlatGroupsLayers(state.project.layers).layers;
    for (const layer of layers.filter(
      layer => layer.type === 'features_layer' && layer.has_permission && !!layer.geometry_type
    )) {
      Vue.set(layer, 'labels_visible', visible);
    }
  },
  SET_LAYER_LABELS_VISIBILITY: (state, value) => {
    const { layerId, visible } = value;
    const layer = findLayerByLayerId(state.project.layers, layerId);
    Vue.set(layer, 'labels_visible', visible);
  },
  SET_LAYER_ARROWS_VISIBILITY: (state, value) => {
    const { layerId, visible } = value;
    const layer = findLayerByLayerId(state.project.layers, layerId);
    Vue.set(layer, 'direction_arrows_visible', visible);
  },
  SET_LAYER_OPACITY: (state, value) => {
    const { layerId, opacity } = value;
    const layer = findLayerByLayerId(state.project.layers, layerId);
    Vue.set(layer, 'opacity', opacity);
  },
  SET_MODULE_OPACITY: (state, value) => {
    const { layerId, opacity } = value;
    console.log(`layerid: ${layerId}, opacity: ${opacity} `);
  },
  SET_LAYER_STYLE: (state, value) => {
    const { layerId, style } = value;
    const layer = findLayerByLayerId(state.project.layers, layerId);
    Vue.set(layer, 'style', style);
  },
  SET_LAYER_STYLE_KEY_VALUE: (state, value) => {
    const { layerId, key, keyValue } = value;
    const layer = findLayerByLayerId(state.project.layers, layerId);
    Vue.set(layer, 'style', { ...layer.style, [key]: keyValue });
  },
  SET_LAYER_STYLE_CLASS_KEY_VALUE: (state, value) => {
    const { layerId, classIndex, classValue, prop = 'visible', propValue } = value;
    const layer = findLayerByLayerId(state.project.layers, layerId);
    if (Object.prototype.hasOwnProperty.call(value, 'classIndex')) {
      const ranges = layer.style.ranges;
      if (ranges?.values?.[classIndex]) {
        ranges.values[classIndex][prop] = propValue;
        Vue.set(layer, 'style', { ...layer.style, ranges });
      }
    } else if (Object.prototype.hasOwnProperty.call(value, 'classValue')) {
      const uniques = layer.style.uniques;
      if (uniques?.values?.[classValue]) {
        uniques.values[classValue][prop] = propValue;
        Vue.set(layer, 'style', { ...layer.style, uniques });
      }
    }
  },
  ADD_PROJECT: (state, value) => {
    Vue.set(state.projects, value.id, value);
  },
  DISCARD_PROJECT: (state, value) => {
    Vue.set(state.projects, value.id, value);
  },
  SET_PROJECTS: (state, data) => {
    const projects = arrayToObject(data);
    const defaultProject = JSON.parse(JSON.stringify(state.projects['default'] || ''));
    Vue.set(
      state,
      'projects',
      Object.keys(projects).reduce((total, current) => {
        const { layers } = state.projects[current] || {};
        const stateProjectData = {
          ...(layers ? { layers } : {}),
        };
        return { ...total, [current]: { ...projects[current], ...stateProjectData } };
      }, {})
    );
    if (defaultProject && !state.projects['default']) Vue.set(state.projects, 'default', defaultProject);
  },
  SET_PROJECT_NAME: (state, value) => {
    state.project.name = value;
    state.projects[state.project.id].name = value;
  },
  DELETE_PROJECT: (state, value) => {
    Vue.delete(state.projects, value);
  },
  DELETE_PROJECT_LAYER: (state, value) => {
    const { parentIndex, index } = findLayerIndexByLayerId(state.project.layers, value);
    if (!parentIndex && parentIndex !== 0) {
      Vue.delete(state.project.layers, index);
    } else {
      const groupLayers = state.project.layers[parentIndex].layers;
      Vue.delete(groupLayers, index);
    }
  },
  DELETE_PROJECT_GROUP: (state, value) => {
    Vue.delete(state.project.layers, value);
  },
  SET_PROJECT_LAYERS_ORDER(state, value) {
    state.project.layers = value;
  },
  SET_PROJECT_BASEMAP(state, value) {
    Vue.set(state.project, 'basemap_layer', value);
  },
  SET_LAYERS_COUNT: (state, value) => {
    const { dataSource, count } = value;
    Vue.set(state.layersCount, dataSource, count);
  },
  // ADD_ATTRIBUTE: (state, value) => {
  //   state.metadata[value.dataSourceName].attributes_schema.attributes.push(value.data);
  // },
  DELETE_ATTRIBUTE: (state, value) => {
    const newLayersState = Object.entries(state.layers).reduce((total, [key, layer]) => {
      if (layer.data_source_name === value.dataSourceName) {
        const filteredAttributesSchema = layer.form_schema.elements.map(group => {
          return {
            ...group,
            elements: group.elements.filter(attribute => attribute.attribute !== value.attributeName),
          };
        });
        total[key] = { ...layer, form_schema: { elements: filteredAttributesSchema } };
      } else {
        total[key] = layer;
      }
      return total;
    }, {});
    Vue.set(state, 'layers', newLayersState);
    const attributes = state.metadata[value.dataSourceName].attributes_schema.attributes;
    state.metadata[value.dataSourceName].attributes_schema.attributes = attributes.filter(
      a => a.name !== value.attributeName
    );
  },
  // EDIT_ATTRIBUTE: (state, { dataSourceName, attributeName, data }) => {
  //   const { measurement_unit_id, ...restData } = data;
  //   const attributeIndex = state.metadata[dataSourceName].attributes_schema.attributes.findIndex(
  //     attribute => attribute.name === attributeName
  //   );
  //   if (attributeIndex >= 0)
  //     Vue.set(state.metadata[dataSourceName].attributes_schema.attributes, attributeIndex, {
  //       ...state.metadata[dataSourceName].attributes_schema.attributes[attributeIndex],
  //       ...restData,
  //       ...(Object.keys(data).includes('measurement_unit_id') ? { measurement_unit: { id: measurement_unit_id } } : {}),
  //     });
  // },
  SET_ATTRIBUTE: (state, value) => {
    const attributes = state.metadata[value.payload.dataSourceName].attributes_schema.attributes.map(a => {
      return a.name === value.payload.attributeName ? value.data : a;
    });
    state.metadata[value.payload.dataSourceName].attributes_schema.attributes = attributes;
  },
  ADD_FEATURES_LAYER: (state, payload) => {
    const { id, value } = payload;
    Vue.set(state.featuresLayers, id, value);
  },
  SET_CURRENT_LAYER_SELECTION_FEATURE: (state, payload) => {
    const { feature_id, feature } = payload;
    Vue.set(state.currentLayerSelectedObjects, feature_id, feature);
  },
  SET_CURRENT_LAYER_SELECTION_FEATURES: (state, features) => {
    const crs = features.crs;
    for (const feature of features.features) {
      Vue.set(state.currentLayerSelectedObjects, feature.id, { ...feature, ...{ crs } });
    }
  },
  DELETE_CURRENT_LAYER_SELECTION_FEATURE: (state, feature_id) => {
    Vue.delete(state.currentLayerSelectedObjects, feature_id);
  },
  SELECT_CURRENT_LAYER_OBJECT_IDS: (state, feature_ids) => {
    const featuresIds = Array.from(new Set([...feature_ids, ...state.currentLayerSelectedObjectsIds]));
    state.currentLayerSelectedObjectsIds = featuresIds;
  },
  UNSELECT_CURRENT_LAYER_OBJECT_IDS: (state, features_ids) => {
    const featuresIds = state.currentLayerSelectedObjectsIds.filter(oId => !features_ids.includes(oId));
    state.currentLayerSelectedObjectsIds = featuresIds;
  },
  UNSELECT_ALL_CURRENT_LAYER_OBJECT_ID: state => {
    state.currentLayerSelectedObjectsIds = [];
  },
  SET_EASEMENTS: (state, data) => {
    const { easementsProps, crs } = data;
    const easements = {};
    for (const easement of easementsProps) {
      easements[easement.id] = {
        ...easement.properties,
        ...{ id: easement.id },
        ...{ geometry: { ...easement.geometry, ...{ crs } } },
      };
    }
    state.easements = easements;
  },
  DELETE_EASEMENT: (state, easement_id) => {
    Vue.delete(state.easements, easement_id);
  },
  SET_FOLDERS: (state, data) => {
    const { foldersProps, crs } = data;
    const folders = {};
    for (const folder of foldersProps) {
      folders[folder.id] = {
        ...folder.properties,
        ...{ id: folder.id },
        ...{ geometry: { ...folder.geometry, ...{ crs } } },
      };
    }
    state.folders = folders;
  },
  DELETE_FOLDER: (state, folder_id) => {
    Vue.delete(state.folders, folder_id);
  },
  SET_FOLDER_DOCUMENTS: (state, payload) => {
    const { folder_id, data } = payload;
    Vue.set(state.folderDocuments, folder_id, data);
  },

  SET_RELATION_VALUES_MAPPING: (state, payload) => {
    const { data, data_source_name, attribute_name } = payload;
    if (!state.relationValuesMapping[data_source_name]) {
      state.relationValuesMapping[data_source_name] = {};
    }
    const formattedData = {};
    for (const d of data) {
      formattedData[d.value] = d.display_value;
    }
    state.relationValuesMapping[data_source_name][attribute_name] = formattedData;
  },
  SET_LAYERS_SCHEMA: (state, data) => {
    const groupedGroups = groupByProperty(data, 'schema_scope');
    Vue.set(state, 'layersSchemaByScope', groupedGroups);
  },
  SET_LAYERS_SCHEMA_BY_SCOPE: (state, payload) => {
    const { data, scope } = payload;
    Vue.set(state.layersSchemaByScope, scope, data);
  },
  DELETE_LAYER: (state, { layer_id: id, scope }) => {
    if (scope && state.layersSchemaByScope[scope]) {
      Vue.set(
        state.layersSchemaByScope,
        scope,
        state.layersSchemaByScope[scope].map(group => {
          return { ...group, layers: group.layers.filter(layer => layer.id !== id) };
        })
      );
    }
    Vue.delete(state.layers, id);
  },
  DELETE_LAYER_GROUP: (state, { group_id: id, scope }) => {
    Vue.set(
      state.layersSchemaByScope,
      scope,
      state.layersSchemaByScope[scope].filter(group => group.id !== id)
    );
  },
  EDIT_LAYER_GROUP: (state, { group_id: id, body, scope }) => {
    const { order_in_group: toIndex, ...restBody } = body;
    const currentState = JSON.parse(JSON.stringify(state.layersSchemaByScope[scope]));
    const fromIndex = currentState.findIndex(g => g.id === id);
    if (fromIndex >= 0 && toIndex >= 0) {
      const group = currentState.splice(fromIndex, 1)[0];
      currentState.splice(toIndex - 1, 0, group);
    }
    Vue.set(
      state.layersSchemaByScope,
      scope,
      currentState.map(group => {
        return group.id === id ? { ...group, ...restBody } : group;
      })
    );
  },
  SET_LAYERS_ORDER: (state, { body: { layers, group_id: id, order_in_group: toIndex }, scope }) => {
    const layersIds = layers.map(layer => layer.id);
    const deletedLayers = [];
    const currentState = JSON.parse(JSON.stringify(state.layersSchemaByScope[scope])).map(group => {
      return {
        ...group,
        layers: group.layers.filter(layer => {
          if (layersIds.includes(layer.id)) {
            deletedLayers.push(layer);
            return false;
          } else return true;
        }),
      };
    });
    if (id || id === 0) {
      const group = currentState.find(g => g.id === id);
      group.layers = [...group.layers.slice(0, toIndex - 1), ...deletedLayers, ...group.layers.slice(toIndex - 1)];
      Vue.set(
        state.layersSchemaByScope,
        scope,
        currentState.map(g => {
          return g.id === id ? group : g;
        })
      );
    } else Vue.set(state.layersSchemaByScope, scope, currentState);
  },
  SET_LAYER_VISIBLE: (state, { layer_id: layerId, group_id: groupId, value, scope }) => {
    const layers = state.layersSchemaByScope[scope].find(g => g.id === groupId).layers;
    Vue.set(
      state.layersSchemaByScope[scope].find(g => g.id === groupId),
      'layers',
      layers.map(layer => (layer.id === layerId ? { ...layer, visible: value } : layer))
    );
  },
  SET_LAYERS_GROUP_VISIBLE: (state, { group_id: groupId, value, scope }) => {
    const layers = state.layersSchemaByScope[scope].find(g => g.id === groupId).layers;
    Vue.set(
      state.layersSchemaByScope[scope].find(g => g.id === groupId),
      'layers',
      layers.map(layer => ({ ...layer, visible: value }))
    );
  },
  DELETE_LAYERS_BY_DATA_SOURCE: (state, dataSourceName) => {
    const deletedLayers = objectToArray(state.layers)
      .filter(layer => layer.data_source_name === dataSourceName)
      .map(layer => layer.id);
    state.layers = arrayToObject(
      objectToArray(state.layers).filter(layer => layer.data_source_name !== dataSourceName)
    );
    for (const scope of Object.keys(state.layersSchemaByScope)) {
      if (state.layersSchemaByScope[scope].layers) {
        state.layersSchemaByScope[scope].layers = state.layersSchemaByScope[scope].layers.filter(
          layer => !deletedLayers.includes(layer.id)
        );
      }
      if (state.layersSchemaByScope[scope]) {
        state.layersSchemaByScope[scope] = state.layersSchemaByScope[scope].map(group => {
          return { ...group, layers: group.layers.filter(layer => !deletedLayers.includes(layer.id)) };
        });
      }
    }
  },
  SET_CACHE_LAYERS_METADATA: (state, data) => {
    state.cacheLayersMetadata = arrayToObject(data);
  },
};

const actions = {
  getProjectLayers({ state }) {
    return state.project.layers;
  },
  async getAdditionalLayersMetadata(
    // Data sources without scope are only used in specific cases
    // and are not return in 'get:dataio/data_sources/metadata'
    // so they are requested and stored separately
    { commit },
    { params = { with_attributes_schema: true }, skipDefaultErrorHandler = true } = {}
  ) {
    const dataSourcesNames = ['users_users'];
    const responses = [];
    for await (const dataSourceName of dataSourcesNames) {
      try {
        const r = await api.get(`/dataio/data_sources/${dataSourceName}/metadata`, {
          params,
          skipDefaultErrorHandler,
        });
        responses.push(r);
      } catch {
        //
      }
    }
    const metadata = responses
      .map(el => el.data.data)
      .reduce((total, current) => {
        return { ...total, [current.name]: current };
      }, {});
    commit('SET_ADDITIONAL_METADATA', metadata);
  },
  async getLayersMetadata({ commit, dispatch }, params) {
    const { withAdditionalMetadata, ...paramsRest } = params;
    if (withAdditionalMetadata) await dispatch('getAdditionalLayersMetadata');
    const r = await api.get('v2/datasources', { params: paramsRest });
    const metadata = r.data.data.reduce((total, current) => ({ ...total, [current.name]: current }), {});
    commit('SET_METADATA', metadata);
  },
  async getLayerMetadata({ commit }, payload) {
    const { dataSource, params } = payload;
    const r = await api.get(`v2/datasources/${dataSource}`, { params });
    commit('ADD_METADATA', { dataSource, metadata: r.data.data });
    return { dataSource, metadata: r.data.data };
  },
  async deleteDataSource({ commit }, name) {
    await api.delete(`/dataio/data_sources/${name}`);
    commit('DELETE_DATA_SOURCE', name);
  },
  async deleteDatasourceFeature({ dispatch }, { datasource, id, params }) {
    await api.delete(`/dataio/data_sources/${datasource}/features/${id}`, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
  },
  async deleteDatasourceFeatures({ dispatch }, { datasource, payload, params, useMassEditMethod = true }) {
    /** Using following method for mass editing is related to SRVS-2147 - this action should be reworked when v2 is implemented */
    if (useMassEditMethod)
      await dispatch('editMultipleDataSourceFeatures', {
        params,
        data_source_name: datasource,
        delete: { features_ids: payload.to_delete },
        ...(params?.layer_id && { layer_id: params.layer_id }),
      });
    else {
      await api.delete(`/dataio/data_sources/${datasource}/features`, { data: payload, params });
      await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    }
  },
  async addDatasourceFeature({ dispatch }, { datasource, body, params }) {
    const r = await api.post(`/dataio/data_sources/${datasource}/features`, { feature: body }, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async editDatasourceFeature({ dispatch }, { datasource, id, body, params }) {
    const r = await api.put(`/dataio/data_sources/${datasource}/features/${id}`, { feature: body }, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async transformDatasource({ dispatch }, { datasource, payload }) {
    await api.post(`/dataio/data_sources/${datasource}/transform`, payload);
    await dispatch('getLayersMetadata', {
      with_attributes_schema: true,
      with_layers: true,
      with_last_edit: true,
      with_owner: true,
    });
  },
  async addTopoDatasourceFeature({ dispatch }, { datasource, body, params, skipDefaultErrorHandler = false }) {
    const r = await api.post(`/topological_edit/insert_feature/${datasource}`, body, {
      params,
      skipDefaultErrorHandler,
    });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async editTopoDatasourceFeature({ dispatch }, { datasource, id, body, params }) {
    const r = await api.put(`/topological_edit/update_feature/${datasource}/${id}`, body, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async deleteTopoDatasourceFeatures({ dispatch }, { datasource, payload, params }) {
    const r = await api.delete(`/topological_edit/delete_features/${datasource}`, { data: payload, params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async editMultipleDataSourceFeatures({ dispatch }, { params, ...payload }) {
    const r = await api.post(`/dataio/data_sources/${payload.data_source_name}/features/edit`, payload, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', payload.data_source_name, { root: true });
    return r;
  },
  async invertLine(store, { datasource, id, params }) {
    return await api.post(`/topological_edit/reverse_line/${datasource}/${id}`, { params });
  },
  async splitTopologicalLine({ dispatch }, { datasource, id, body, params }) {
    const r = await api.post(`/topological_edit/split_line/${datasource}/${id}`, body, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async joinTopologicalLines({ dispatch }, { datasource, body, params }) {
    const r = await api.post(`/topological_edit/merge_lines/${datasource}`, body, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async splitLine({ dispatch }, { datasource, id, body, params }) {
    const r = await api.put(`/dataio/data_sources/${datasource}/features/${id}/split`, body, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async joinLines({ dispatch }, { datasource, id, body, params }) {
    const r = await api.put(`/dataio//data_sources/${datasource}/features/${id}/join`, body, { params });
    await dispatch('attributesSchema/updateDatasourceRelationValuesMapping', datasource, { root: true });
    return r;
  },
  async getIntersectingLines(store, { datasource, id }) {
    return await api.get(`/topological_edit/intersecting_lines/${datasource}/${id}`);
  },
  async getTopologicalParentObjects(store, { dataSourceName, attributeName, geometry }) {
    const r = await api.post(`/topological_edit/parent_objects/${dataSourceName}/${attributeName}`, { geometry });
    return r.data.data;
  },
  async getLayerFeature(store, payload) {
    const { layer_id, features_filter, feature_id, skipDefaultErrorHandler = false } = payload;
    const params = {
      with_features: true,
      with_count: true,
      with_features_bbox: false,
      with_collection_bbox: false,
      with_geometry: !payload.only_attributes,
    };
    const r = await api.post(
      `/layers/features_layers/${layer_id}/features/${feature_id}`,
      { features_filter },
      { params, skipDefaultErrorHandler }
    );
    return r.data.data;
  },
  async editDataSourceMetadata({ commit }, payload) {
    const { name, body } = payload;
    await api.put(`v2/datasources/${name}`, body);
    if (body.target_srid) {
      // W backendzie jest inny klucz układu wsp zwracany, a inny w requeście edycji
      // Na razie podmieniam ręcznie, ale docelowo powinny być ujednolicone
      body.srid = body.target_srid;
      delete body.target_srid;
    }
    commit('EDIT_DATA_SOURCE_METADATA', payload);
  },
  /**
   * @param store
   * @param payload
   */
  async getLayerFeatures(store, payload) {
    const { layer_id, features_filter, sorting, params } = payload;
    return await api.post(
      `/layers/features_layers/${layer_id}/features`,
      {
        features_filter,
        sorting,
      },
      {
        params: {
          with_count: true,
          ...params,
        },
      }
    );
  },
  async exportMultipleDatasourceFeatures(store, payload) {
    const { configs, datasource, params, filename, headers } = payload;
    const r = await api.post(`/v2/datasources-download/multiple/${datasource}`, configs, {
      headers,
      params,
      responseType: 'blob',
      timeout: 0,
    });
    saveFileFromBackend(r.data, r.headers, filename);
    return r;
  },
  async exportDatasourceFeatures(store, payload) {
    const { datasource, attributes, features_filter, sorting, params, filename, headers, style } = payload;
    let url = `/v2/datasources-download/${datasource}`;
    if (params.format === 'gpkg') {
      url = `/v2/datasources-download/gpkg/${datasource}`;
      delete params.format;
    }
    const r = await api.post(
      url,
      { attributes, features_filter, sorting, style },
      {
        headers,
        params,
        responseType: 'blob',
        timeout: 0,
      }
    );
    saveFileFromBackend(r.data, r.headers, filename);
    return r;
  },
  async exportLayerFeatures(store, payload) {
    const { layer_id, attributes, features_filter, sorting, params, format } = payload;
    const r = await api.post(
      `/layers/features_layers/${layer_id}/export`,
      { attributes, features_filter, sorting, format },
      {
        params,
        responseType: 'blob',
        timeout: 0,
      }
    );
    saveFileFromBackend(r.data, r.headers);
    return r;
  },
  async getLayerPermissions(store, layerId) {
    return await api.get(`/v2/layers/${layerId}/permissions`);
  },
  async getV2DatasourceFeatures(store, data) {
    const { datasource, payload, params } = data;
    const r = await api.post(`v2/datasources-features/read/${datasource}`, payload, { params: params ?? undefined });
    return r.data.data;
  },
  async getDataSourceFeatures(
    { commit },
    { dataSource, filters, countOnly = false, with_geometry = true, idsOnly = false, idsDescsOnly = false }
  ) {
    /**
     * @property {Boolean} with_features - Czy pobrać obiekty
     * @property {Boolean} with_count - Czy pobrać liczbę obiektów
     * @property {Boolean} with_features_bbox - Czy pobrać bbox dla każdego obiektu
     * @property {Boolean} with_collection_bbox - Czy pobrać bbox dla całego FeatureCollection
     */
    const params = {
      with_features: true,
      with_count: true,
      with_features_bbox: false,
      with_collection_bbox: false,
      with_geometry: with_geometry,
    };
    if (countOnly) {
      params.with_features = false;
    }
    if (idsOnly) {
      params.ids_only = true;
    }
    if (idsDescsOnly) {
      params.ids_descs_only = true;
    }

    const r = await api.post(`/dataio/data_sources/${dataSource}/read`, { features_filter: filters }, { params });
    /**
     * @property {Number} features_count - Liczba elementów
     * @property {Object} features - Obiekty zwracane są jako FeatureCollection
     * @property {Array} bbox - Bbox całego zestwu obiektów
     */
    const data = r.data.data;

    commit('SET_LAYERS_COUNT', { dataSource, count: data.features_count });
    if (countOnly) {
      return data.features_count;
    } else {
      commit('ADD_DATA_SOURCE_FEATURES', {
        dataSource,
        features: data.features,
      });
      return idsOnly || idsDescsOnly ? data.ids : data.features;
    }
  },
  async getNearestNeighboursDataSourceFeatures(
    { commit },
    {
      dataSource,
      filters,
      geometry,
      nearest_neighbours_buffer,
      countOnly = false,
      with_geometry = true,
      idsOnly = false,
      idsDescsOnly = false,
      limit = 15,
    }
  ) {
    /**
     * @property {Boolean} with_features - Czy pobrać obiekty
     * @property {Boolean} with_count - Czy pobrać liczbę obiektów
     * @property {Boolean} with_features_bbox - Czy pobrać bbox dla każdego obiektu
     * @property {Boolean} with_collection_bbox - Czy pobrać bbox dla całego FeatureCollection
     */
    const params = {
      with_features: true,
      with_count: true,
      with_features_bbox: false,
      with_collection_bbox: false,
      with_geometry: with_geometry,
      limit: limit,
    };
    if (countOnly) {
      params.with_features = false;
    }
    if (idsOnly) {
      params.ids_only = true;
    }
    if (idsDescsOnly) {
      params.ids_descs_only = true;
    }

    const r = await api.post(
      `/dataio/data_sources/${dataSource}/nearest_neighbours`,
      {
        features_filter: filters,
        geometry,
        nearest_neighbours_buffer,
      },
      { params }
    );
    /**
     * @property {Number} features_count - Liczba elementów
     * @property {Object} features - Obiekty zwracane są jako FeatureCollection
     * @property {Array} bbox - Bbox całego zestwu obiektów
     */
    const data = r.data.data;

    commit('SET_LAYERS_COUNT', { dataSource, count: data.features_count });
    if (countOnly) {
      return data.features_count;
    } else {
      // eslint-disable-next-line
      commit('ADD_DATA_SOURCE_FEATURES', {
        dataSource,
        features: data.features,
      });
      return idsOnly || idsDescsOnly ? data.ids : data.features;
    }
  },
  async getV2DatasourceFeature(store, data) {
    const { datasource, featureId, payload, params } = data;
    const r = await api.post(`v2/datasources-features/read/${datasource}/feature/${featureId}`, payload, {
      params: params ?? undefined,
    });
    return r.data.data;
  },
  async getDataSourceFeature(state, payload) {
    const { dataSource, feature_id, params } = payload;
    const customParams = params ?? {
      with_features_bbox: true,
    };
    const r = await api.post(`/dataio/data_sources/${dataSource}/read/${feature_id}`, {}, { params: customParams });
    return r.data.data;
  },
  async getProjects({ commit, dispatch, rootState }, params = {}) {
    const promises = [
      api.get(`v2/projects`, {
        params: {
          ...params,
          all_users: rootState.users.currentUser.is_superadmin ? true : undefined,
          with_default: false,
        },
      }),
      ...(params.with_default ? [dispatch('getProject', { id: 'default' })] : []),
    ];
    const [r] = await Promise.all(promises);
    commit('SET_PROJECTS', r.data.data);
    return r.data.data;
  },
  async getProject({ state, commit, dispatch }, { id, params, silent = false, config = {} }) {
    if (!state.layers || Object.keys(state.layers).length === 0) await dispatch('getLayers');
    if (id === 'default') {
      const defaultProject = await dispatch('getDefaultProject', silent);
      if (silent) return defaultProject;
      else return;
    }
    const r = (await api.get(`v2/projects/${id}`, { params, ...config })).data.data;
    const project = mapProject(r, state.layers);
    if (silent) {
      return project;
    } else {
      commit('ADD_PROJECT', project);
    }
  },
  async getDataSourceForm({ commit }, dataSource) {
    const r = await api.get(`/dataio/forms/${dataSource}/default`);
    commit('ADD_DATA_SOURCE_FORM', { dataSource, form: r.data.data });
    return r;
  },
  async editProject({ dispatch }, { body, id }) {
    if (id === 'default') {
      dispatch('editDefaultProject', body);
      return;
    }
    await api.put(`v2/projects/${id}`, body);
    await Promise.all([dispatch('getProjects'), dispatch('getProject', { id })]);
  },
  async deleteProject({ commit }, id) {
    const r = await api.delete(`v2/projects/${id}`);
    commit('DELETE_PROJECT', id);
    return r;
  },
  async shareProject(store, { userIds, id }) {
    return await api.post(`v2/projects-sharing/${id}/set`, userIds);
  },
  async unshareProject(store, { userIds, id }) {
    return await api.post(`v2/projects-sharing/${id}/unset`, userIds);
  },
  async getProjectSharedUsers(store, id) {
    const r = await api.get(`v2/projects-sharing/${id}/shared_to`);
    return r.data.data.map(data => data.id);
  },
  async getDefaultProject({ state, commit }, silent = false) {
    const r = (await api.get('v2/projects-default')).data.data;
    const defaultProject = mapProject({ ...r, id: 'default' }, state.layers);
    if (silent) {
      return defaultProject;
    } else {
      commit('ADD_PROJECT', defaultProject);
    }
  },
  async setDefaultProject({ dispatch }, id) {
    await api.post(`v2/projects-default/${id}`);
    await dispatch('getProjects', { with_default: true });
  },
  async editDefaultProject({ dispatch }, body) {
    await api.put('v2/projects-default', body);
    await dispatch('getDefaultProject');
  },
  async setPredefinedProject({ dispatch }, id) {
    await api.post(`v2/projects-predefined/${id}/set`);
    await dispatch('getProjects', { with_default: true });
  },
  async unsetPredefinedProject({ dispatch }, id) {
    await api.post(`v2/projects-predefined/${id}/unset`);
    await dispatch('getProjects', { with_default: true });
  },
  async createProject({ commit, dispatch, state }, payload) {
    const r = await api.post(`v2/projects`, payload);
    await dispatch('getProjects', { with_default: true });
    const project = state.projects[r.data.data];
    commit('SET_PROJECT', project);
    return project?.id || null;
  },
  async getLayers({ commit }, params) {
    const r = await api.get('v2/layers', { params });
    const metadata = r.data.data.reduce(
      (acc, layer) => {
        if (layer.layer_scope === 'basemap') acc.basemaps[layer.id] = layer;
        else acc.layers[layer.id] = layer;
        return acc;
      },
      {
        layers: {},
        basemaps: {},
      }
    );
    commit('SET_LAYERS', metadata.layers);
    if (Object.keys(metadata.basemaps).length) {
      commit('SET_BASEMAP_LAYERS', metadata.basemaps);
    }
  },
  async getDataTypes({ commit }) {
    const r = await api.get('dataio/data_types');
    commit('SET_DATA_TYPES', r.data.data);
  },
  async addAttribute({ dispatch }, { data, dataSourceName }) {
    // It's to buggy for now to add attribute to store by frontend
    // Attribute keys are different in payloads and in responses
    // Thats why 'getLayerMetadata' action is called instead of using 'ADD_ATTRIBUTE' mutation for now
    await api.post(`v2/datasources-attributes/${dataSourceName}`, data);
    await Promise.all([
      dispatch('getDataSourceForm', dataSourceName),
      dispatch('getLayerMetadata', { dataSource: dataSourceName }),
    ]);
    // commit('ADD_ATTRIBUTE');
  },
  async editAttribute({ dispatch }, payload) {
    const { data, dataSourceName, attributeName } = payload;
    // It's to buggy for now to edit attribute in store by frontend
    // Attribute keys are different in payloads and in responses
    // Thats why 'getLayerMetadata' action is called instead of using 'EDIT_ATTRIBUTE' mutation for now
    await api.put(`v2/datasources-attributes/${dataSourceName}/${attributeName}`, data);
    await dispatch('getLayerMetadata', { dataSource: dataSourceName });
    // commit('EDIT_ATTRIBUTE');
  },
  async deleteAttribute({ commit, dispatch }, payload) {
    const { attributeName, dataSourceName } = payload;
    await api.delete(`v2/datasources-attributes/${dataSourceName}/${attributeName}`);
    await dispatch('getDataSourceForm', dataSourceName);
    commit('DELETE_ATTRIBUTE', payload);
  },
  async getLayersSchema({ commit }) {
    const r = await api.get('v2/layers-schema');
    commit('SET_LAYERS_SCHEMA', r.data.data);
  },
  async getLayersSchemaByScope({ commit }, { scope = 'core' }) {
    const r = await api.get(`v2/layers-schema/${scope}`);
    commit('SET_LAYERS_SCHEMA_BY_SCOPE', { data: r.data.data, scope });
  },
  async addNewLayersGroup({ dispatch }, payload) {
    const { scope, ...rest } = payload;
    await api.post(`v2/layers-groups`, { ...rest, schema_scope: scope });
    await dispatch('getLayersSchemaByScope', { scope });
  },
  async editLayerGroup({ commit }, { group_id, body, scope }) {
    await api.put(`v2/layers-groups/${group_id}`, body);
    commit('EDIT_LAYER_GROUP', { group_id, body, scope });
  },
  async deleteLayerGroup({ commit }, { group_id, scope }) {
    await api.delete(`v2/layers-groups/${group_id}`);
    commit('DELETE_LAYER_GROUP', { group_id, scope });
  },
  async attachMultipleLayerGroup({ commit, dispatch }, { body, scope, params }) {
    await api.put(`v2/layers-schema/${scope}`, body, { params });
    commit('SET_LAYERS_ORDER', { body, scope });
    const promises = [...(scope === 'cache' ? [dispatch('getCacheLayersMetadata')] : [])];
    await Promise.all(promises);
  },
  async attachLayerGroup({ dispatch }, { layer_id, body, scope, params }) {
    await api.put(`layers/schema/${scope}/${layer_id}`, body, { params });
    const promises = [
      dispatch('getLayersSchemaByScope', { scope }),
      ...(scope === 'cache' ? [dispatch('getCacheLayersMetadata')] : []),
    ];
    await Promise.all(promises);
  },
  async setLayerVisibility({ commit }, { layer_id, group_id, params, schema_scope }) {
    await api.put(`v2/layers-groups/${group_id}/${layer_id}/visible`, {}, { params });
    commit('SET_LAYER_VISIBLE', { layer_id, group_id, value: params.visible, scope: schema_scope });
  },
  async setLayersGroupVisibility(store, { group_id, params, schema_scope }) {
    await api.put(`v2/layers-groups/${group_id}/visible`, {}, { params });
    store.commit('SET_LAYERS_GROUP_VISIBLE', { group_id, value: params.visible, scope: schema_scope });
  },
  async addLayer({ dispatch }, payload) {
    const {
      layer_type,
      skipMetadataParsing,
      body: { data_source, ...bodyRest },
      embedded = null,
    } = payload;
    // Backend changed 'data_source' to 'datasource' in v2 routing
    // but all components and datasources/layers copying are working with 'data_source'
    const r = await api.post(`v2/${layer_type}${embedded !== null ? `/embedded/${embedded}` : ''}`, {
      ...bodyRest,
      datasource: data_source,
    });
    if (!skipMetadataParsing) {
      await Promise.all([
        dispatch('getLayers', { scope: bodyRest.layer_scope }),
        dispatch('getLayersSchemaByScope', { scope: bodyRest.layer_scope }),
      ]);
    }
    return r.data.data;
  },
  async editLayer({ commit, dispatch }, { layer_id, layer_type, body, scope, skipMetadataParsing }) {
    if (layer_type === 'features_layers') {
      // For now only features_layers are in api v2
      await api.put(`v2/features-layers/${layer_id}`, body);
    } else {
      await api.put(`layers/${layer_type}/${layer_id}`, body);
    }
    if (!skipMetadataParsing) {
      if (layer_type === 'features_layers') {
        // For now only features_layers got new manager and data updating by frontend
        commit('SET_LAYER', { layer_id, body, scope });
      } else {
        await Promise.all([
          dispatch('getLayers', { scope }),
          ...(scope !== 'module' ? [dispatch('getLayersSchemaByScope', { scope })] : []),
        ]);
      }
    }
  },
  async deleteLayer({ dispatch }, { layer_id, layer_type, scope }) {
    await api.delete(`layers/${layer_type}/${layer_id}`);
    await Promise.all([dispatch('getLayers', { scope }), dispatch('getLayersSchemaByScope', { scope })]);
  },
  async deleteLayerWithoutUpdate({ commit }, { layer_id, layer_type, scope }) {
    await api.delete(`v2/${layer_type}/${layer_id}`);
    commit('DELETE_LAYER', { layer_id, scope });
  },
  async getExpressions({ commit }) {
    const r = await api.get('/dataio/boolean_expressions/metadata');
    r.data.data.push(
      {
        name: 'IS_NULL',
        type: 'operator',
        verbose_name: 'IS NULL',
        allowed_rhs_data_types: { name: 'any' },
        isSpecial: true,
      },
      {
        name: 'IS_NOT_NULL',
        type: 'operator',
        verbose_name: 'IS NOT NULL',
        allowed_rhs_data_types: { name: 'any' },
        isSpecial: true,
        negationName: 'IS_NULL',
      },
      {
        name: '$IN',
        type: 'operator',
        verbose_name: i18n.t('inputs.inOperator'),
        allowed_filter_data_types: ['text', 'integer'],
      },
      {
        name: 'NOT_IN',
        type: 'operator',
        verbose_name: i18n.t('inputs.notInOperator'),
        allowed_filter_data_types: ['text', 'integer'],
        negationName: '$IN',
      }
    );
    commit('SET_EXPRESSIONS', r.data.data);
  },
  async validateExpression(store, payload) {
    const { params, body } = payload;
    return await api.post('dataio/boolean_expressions/validate', body, { params });
  },
  async getLayerUniqueStyle(store, payload) {
    const { layer_id, attribute_name, signature_style = false } = payload;
    return await api.get(`v2/features-layers/${layer_id}/style_classes/${attribute_name}`, {
      params: { signature_style },
    });
    // return await api.get(`/layers/features_layers/${layer_id}/style_classes/${attribute_name}`, {
    //   params: { signature_style },
    // });
  },
  async getDataSourceAttributeUniqueValues(store, payload) {
    const { dataSourceName, attributeName, params = {} } = payload;
    const r = await api.post(`/dataio/data_sources/${dataSourceName}/unique_values/${attributeName}`, params);
    return r.data.data;
  },
  async getIcons({ commit }, params = {}) {
    const { templates = false } = params;
    const r = await api.get('v2/icons', { params: { templates } });
    !templates ? commit('SET_ICONS', r.data.data) : commit('SET_TEMPLATE_ICONS', r.data.data);
  },
  async deleteIcon({ dispatch, state }, name) {
    await api.delete(`v2/icons/${name}`);
    await dispatch('getIcons', { templates: state.templateIcons.find(icon => icon.name === name) ? true : false });
  },
  async getIcon(store, name) {
    const r = await api.get(`v2/icons/${name}`);
    return r.data;
  },
  async addIcon({ dispatch }, formData) {
    await api.post('v2/icons', formData, {
      noWrap: true,
      headers: {
        'Content-Type': 'multipart/form-data;',
      },
    });
    await dispatch('getIcons');
  },
  async getWmsCapabilities(store, payload) {
    return await api.post('/layers/service_layers/capabilities', payload);
  },
  async getDataboxServiceLayers({ commit }) {
    const r = await api.get('databox/service_layers');
    commit('SET_DATABOX_SERVICE_LAYERS', r.data.data);
  },
  async getDataboxMvtLayers({ commit }) {
    const r = await api.get('databox/vector_tiles');
    commit('SET_DATABOX_MVT_LAYERS', r.data.data);
  },
  async getFeaturesLayer({ commit }, layer_id) {
    const r = await api.get(`/layers/features_layers/${layer_id}`);
    commit('ADD_FEATURES_LAYER', { id: layer_id, value: r.data.data });
  },
  async deleteQgisStyle({ dispatch }, { layer_id, scope }) {
    await api.delete(`v2/features-layers/${layer_id}/style_qgis`);
    await Promise.all([dispatch('getLayers', { scope }), dispatch('getLayersSchemaByScope', scope)]);
  },
  async getDefaultValuesPolicies({ commit }) {
    const r = await api.get(`/dataio/default_values/variable_policies`);
    commit('SET_DEFAULT_VALUES_POLICIES', r.data.data);
  },
  async getLayersBbox(store, payload) {
    return (await api.post('/layers/features_layers/bbox', payload)).data.data;
  },
  async setAttribute({ commit }, payload) {
    const { attributeName, dataSourceName, type, value } = payload;
    const r = await api.put(`/dataio/data_sources/${dataSourceName}/${attributeName}`, { [type]: value });
    commit('SET_ATTRIBUTE', { payload, data: r.data.data });
    return r;
  },
  async getMeasurementUnits({ commit }) {
    const r = await api.get('/measurement_units');
    commit('SET_MEASUREMENT_UNITS', r.data.data);
  },
  async getTile(store, payload) {
    const { layerId, envelope, features_filter, attributes, zxy } = payload;
    return await api.post(
      // `/layers/features_layers/${layerId}/mvt`,
      `/mvt_service/${layerId}`,
      { features_filter, envelope, attributes, zxy, use_cache: true },
      { responseType: 'arraybuffer', skipDefaultErrorHandler: true }
    );
  },
  async getCurrentLayerSelectionFeature({ commit }, payload) {
    const { layer_id, feature_id } = payload;
    const r = await api.post(`/layers/features_layers/${layer_id}/features/${feature_id}`, {});
    commit('SET_CURRENT_LAYER_SELECTION_FEATURE', { feature_id, feature: r.data.data });
  },
  async addEasement({ dispatch }, feature) {
    const r = await api.post('/transmission_easement', { feature });
    await dispatch('getEasements');
    return r;
  },
  async getEasement(store, id) {
    const r = await api.post(`/transmission_easement/${id}/read`, {});
    return r.data.data;
  },
  async getEasements({ commit }) {
    const r = await api.post('transmission_easement/read', {});
    commit('SET_EASEMENTS', {
      easementsProps: r.data.data.features.features,
      crs: r.data.data.features.crs,
    });
  },
  async getWatSewCollisions(state, data) {
    const { layerId, featureId, bufferValue } = data;
    const r = await api.post(`transmission_easement_2/${layerId}/features/${featureId}`, {
      ...(bufferValue ? { buffer: bufferValue } : {}),
    });
    return r.data.data;
  },
  async deleteEasement({ commit }, easement_id) {
    await api.delete(`/transmission_easement/${easement_id}`);
    commit('DELETE_EASEMENT', easement_id);
  },
  async editEasement({ dispatch }, payload) {
    const { feature, easement_id } = payload;
    await api.put(`/transmission_easement/${easement_id}`, { feature });
    await dispatch('getEasements');
  },
  async addFolder({ dispatch }, feature) {
    const r = await api.post('/folders', { feature });
    await dispatch('getFolders');
    return r;
  },
  async getFolder(store, id) {
    const r = await api.get(`/folders/${id}`);
    return r.data.data;
  },
  async getFolders({ commit }) {
    const r = await api.post('/folders/read', {});
    commit('SET_FOLDERS', {
      foldersProps: r.data.data.features.features,
      crs: r.data.data.features.crs,
    });
  },
  async deleteFolder({ commit }, folder_id) {
    await api.delete(`/folders/${folder_id}`);
    commit('DELETE_FOLDER', folder_id);
  },
  async editFolder({ dispatch }, payload) {
    const { feature, folder_id } = payload;
    await api.put(`/folders/${folder_id}`, { feature });
    await dispatch('getFolders');
  },
  async getFolderSummary() {
    const r = await api.get('/folders/summary');
    return r.data.data;
  },

  async findDocuments(store, searchString) {
    const params = {
      document_name: searchString,
    };
    const r = await api.get('folders/documents', { params });
    return r.data.data;
  },
  async getFolderDocuments({ commit }, folder_id) {
    const r = await api.get(`/folders/${folder_id}/documents`);
    commit('SET_FOLDER_DOCUMENTS', { folder_id, data: r.data.data });
  },
  async deleteFolderDocument({ dispatch }, payload) {
    const { document_id, folder_id } = payload;
    await api.delete(`/folders/documents/${document_id}`);
    await dispatch('getFolderDocuments', folder_id);
  },
  async downloadDocument(store, document_id) {
    const r = await api.get(`folders/documents/${document_id}/file`, { timeout: 0, responseType: 'arraybuffer' });
    saveFileFromBackend(r.data, r.headers);
  },
  async downloadFolder(store, folder_id) {
    const r = await api.get(`folders/${folder_id}/documents/zip`, {
      responseType: 'arraybuffer',
    });
    saveFileFromBackend(r.data, r.headers);
  },
  async editDocumentName({ dispatch }, payload) {
    const { document_id, folder_id, body } = payload;
    await api.put(`/folders/documents/${document_id}`, body);
    await dispatch('getFolderDocuments', folder_id);
  },
  async getDataSourceAttributeStatistics(store, payload) {
    const { dataSourceName, attributeName, params } = payload;
    const r = await api.post(`v2/datasources-attributes/${dataSourceName}/statistics/${attributeName}`, params || {});
    return r.data.data;
  },
  async addDataSource({ dispatch }, data) {
    const { payload, skipMetadataParsing, params, scope = 'core' } = data;
    const r = await api.post('v2/datasources', payload, { params });
    if (!skipMetadataParsing)
      await dispatch('getLayersMetadata', {
        with_attributes_schema: true,
        with_layers: true,
        with_last_edit: true,
        with_owner: true,
      });
    if (params?.create_layer)
      await Promise.all([dispatch('getLayers', { scope }), dispatch('getLayersSchemaByScope', { scope })]);
    return r.data.data;
  },
  async convertDatasourceToSingle({ dispatch }, datasourceName) {
    await api.post(`dataio/data_sources/${datasourceName}/geometry/to_single`);
    await dispatch('getLayersMetadata', {
      with_attributes_schema: true,
      with_layers: true,
      with_last_edit: true,
      with_owner: true,
    });
  },
  async copyDataSourceData({ dispatch }, payload) {
    const { fromDataSource, toDataSource } = payload;
    await api.post(`v2/datasources-features/copy/${fromDataSource}/${toDataSource}`);
    await dispatch('getLayersMetadata', {
      with_attributes_schema: true,
      with_layers: true,
      with_last_edit: true,
      with_owner: true,
    });
  },
  async uploadDataSource({ dispatch }, formData) {
    await api.post('v2/datasources-upload/geojson', formData, {
      noWrap: true,
      headers: { 'Content-Type': 'multipart/form-data;' },
    });
    const newDataSource = formData.get('datasource_name');
    if (newDataSource) await dispatch('getLayersMetadata', { with_attributes_schema: true });
  },
  async getLayersFeaturesInBbox(state, { active_features_filter, active_layer_id, bbox, ids }) {
    const r = await api.post(`/layers/features_layers/geojson`, { active_features_filter, active_layer_id, bbox, ids });
    return r.data.data;
  },
  async getVehicleHistory(state, data) {
    const { featureId, payload } = data;
    const r = await api.post(`vehicles/${featureId}/location_history`, payload);
    return r.data.data;
  },
  async getVehicleHistoryShp(state, data) {
    const { featureId, payload } = data;
    const r = await api.post(`vehicles/${featureId}/location_history/export`, payload, {
      timeout: 0,
      responseType: 'arraybuffer',
    });
    saveFileFromBackend(r.data, r.headers);
  },
  async exportGeojsonToShapefile(state, payload) {
    const r = await api.post('dataio/data_sources/geojson/export/shp', payload, {
      timeout: 0,
      responseType: 'arraybuffer',
    });
    saveFileFromBackend(r.data, r.headers);
  },
  async getLocationSelection(state, payload) {
    const r = await api.post('select_by_location', payload);
    return r.data.data;
  },
  async syncLayerMbTiles({ dispatch }, id) {
    await api.post(`sync/tiles/${id}`, {});
    await dispatch('getCacheLayersMetadata');
  },
  async getCacheLayersMetadata({ commit }) {
    const r = await api.get('layers/cache/metadata');
    commit('SET_CACHE_LAYERS_METADATA', r.data.data);
  },
  async editCacheLayerMetadata({ dispatch }, data) {
    const { payload, id } = data;
    await api.put(`layer/cache/metadata/${id}`, payload);
    await dispatch('getCacheLayersMetadata');
  },
  async clearMvtLayerCache(store, params) {
    await api.get('misc/clear_cache_mvt_layer', { params });
  },
  async getInfrastructureCollisions(store, payload) {
    return await api.post('infrastructure_collisions', payload);
  },
  async uploadCsvToDataSource(store, { formData, datasourceName } = {}) {
    await api.post(`v2/datasources-upload/csv/${datasourceName}`, formData, {
      noWrap: true,
      headers: { 'Content-Type': 'multipart/form-data;' },
    });
  },
  async readUploadedFile(store, { formData, srid } = {}) {
    const r = await api.post('v2/datasources-upload/read', formData, {
      noWrap: true,
      headers: {
        'Content-Type': 'multipart/form-data;',
        'X-Request-SRID': srid ?? defaultSRID,
      },
    });
    return r.data.data;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
